import { useCallback, useState } from "react";
import { EnableTotpRepositoryImpl } from "../../infrastructure/repositories/EnableTotpRepositoryImpl";
import { SessionManager } from "../../infrastructure/session/SessionManager";

/** Temp tokens from verify-otp / verify-totp for these purposes last 15 minutes. */
export const STEP_UP_TOKEN_TTL_MS = 15 * 60 * 1000;

export const requireAccessToken = (): string => {
  const accessToken = new SessionManager().getAccessToken();
  if (!accessToken) {
    throw new Error("Your session has expired. Please log in again.");
  }
  return accessToken;
};

/**
 * Re-verifies a signed-in user by email OTP or authenticator to get a temp
 * token for `purpose`
 * (CHANGE_PASSWORD, CHANGE_OTP_PREFERENCE). The temp token is returned to the
 * caller rather than stored, so it never replaces the session's tokens.
 */
export const useStepUpVerification = (purpose: string) => {
  const [loading, setLoading] = useState(false);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      return await action();
    } finally {
      setLoading(false);
    }
  }, []);

  const sendEmailCode = useCallback(
    (email: string) =>
      run(async () => {
        await new EnableTotpRepositoryImpl().sendEmailTotp(
          email,
          purpose,
          requireAccessToken(),
        );
      }),
    [run, purpose],
  );

  const verifyEmailCode = useCallback(
    (email: string, code: string) =>
      run(async () => {
        const tokens = await new EnableTotpRepositoryImpl().verifyOtp(
          email,
          code,
          purpose,
          requireAccessToken(),
        );
        if (!tokens.temp_access_token) {
          throw new Error("Verification failed. Please try again.");
        }
        return tokens.temp_access_token;
      }),
    [run, purpose],
  );

  const verifyAuthenticatorCode = useCallback(
    (email: string, code: string) =>
      run(async () => {
        const tokens = await new EnableTotpRepositoryImpl().verifyTotp(
          email,
          code,
          purpose,
          requireAccessToken(),
        );
        if (!tokens.temp_access_token) {
          throw new Error("Verification failed. Please try again.");
        }
        return tokens.temp_access_token;
      }),
    [run, purpose],
  );

  return {
    sendEmailCode,
    verifyEmailCode,
    verifyAuthenticatorCode,
    loading,
  };
};
